// Nearest scrollable ancestor. Prefers the OverlayScrollbars viewport (every
// dashboard scroll surface is OS-based) but falls back to any ancestor whose
// computed overflow scrolls, so it doesn't hinge on one attribute.
//
// Lives here rather than in a component because the trace-detail readings all
// need it to find the panel's viewport, and TraceWorkFeed is imported BY
// ContextPanel — a component-level export would be a cycle. Kept out of
// virtualWindow.js on purpose: that module is deliberately DOM-free so its
// geometry can be tested with synthetic rects.
export function findScrollParent(node) {
    let el = node?.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
        if (el.matches?.("[data-overlayscrollbars-viewport], .os-viewport")) return el;
        const oy = getComputedStyle(el).overflowY;
        if (oy === "auto" || oy === "scroll" || oy === "overlay") return el;
        el = el.parentElement;
    }
    return null;
}

// The reading switcher is sticky at the top of the trace panel's scroll
// viewport, so a row landed flush at scrollTop sits UNDERNEATH it. Every
// landing on a turn has to clear the bar plus a little air, hence one shared
// inset rather than a per-reading guess. The bar is absent in the Lanes
// takeover and in jsdom, where the gap alone is the answer.
export const STICKY_LANDING_GAP_PX = 8;

export function stickyLandingInset(doc = typeof document === "undefined" ? null : document) {
    const bar = doc?.querySelector?.("[data-reading-switcher]");
    return (bar?.offsetHeight || 0) + STICKY_LANDING_GAP_PX;
}

// Where the viewport has to be scrolled for `el` to be readable.
//
// "top" puts the row just under the sticky stack — right for the Context
// reading, where you read the conversation downward from the turn you chose.
//
// "center" parks it in the middle of the space BELOW that stack, and is right
// for the readings that layer their own sticky chrome on top of the switcher
// (the X-ray feed stacks a coverage line and an episode header over it). There
// the exact height of the stack is a moving target, and a row aligned to a
// mis-measured top edge is a row hidden behind that chrome. Centering only has
// to be approximately right to be readable, and it also shows what surrounds
// the row, which is the point of landing there at all.
function clampGoal(viewport, goal) {
    return Math.max(0, Math.min(goal, viewport.scrollHeight - viewport.clientHeight));
}

// Turn a content-space row top into a scrollTop, clearing the sticky stack.
function goalFor(viewport, doc, top, align, rowH) {
    const inset = stickyLandingInset(doc);
    const below = viewport.clientHeight - inset;
    return clampGoal(viewport, align === "center" && rowH < below
        ? top - inset - (below - rowH) / 2
        : top - inset);
}

export function rowLandingTop(el, align = "top") {
    const viewport = el && typeof el.getBoundingClientRect === "function" ? findScrollParent(el) : null;
    if (!viewport) return null;
    const rect = el.getBoundingClientRect();
    const top = viewport.scrollTop + (rect.top - viewport.getBoundingClientRect().top);
    return goalFor(viewport, el.ownerDocument, top, align, rect.height);
}

// Land `el` now. Not element.scrollIntoView: that aligns flush to the viewport
// edge with no way to offset past sticky chrome, which is exactly the bug, and
// jsdom does not implement it at all.
export function scrollRowIntoView(el, align = "top") {
    const goal = rowLandingTop(el, align);
    if (goal == null) return;
    findScrollParent(el).scrollTop = goal;
}

// Input that means the reader has taken the scroll back from a landing.
export const READER_TAKEOVER_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];

// Land on a row and keep aiming until it settles. Returns a cancel function.
//
// One shot is not enough: a reading grows under the target as its async parts
// arrive (the X-ray feed's clock readings, artifact groups, previews), so the
// position computed at frame 0 is stale by the time the reader looks.
//
// The landing ends on the reader's own input, never on a scroll-position
// delta. The browser clamps scrollTop whenever measurement shrinks the
// content, and reading that as intent would abort the landing exactly while it
// is still converging.
//
// For a VIRTUALIZED list this is the wrong tool: the row is not in the DOM
// until the viewport is near it, and no amount of re-aiming at a missing
// element will get there. Those lists land themselves (VirtualList's
// scrollToIndex), because only the list holds the height model.
export function landOnRow(getEl, { align = "top", budgetMs = 600 } = {}) {
    if (typeof window === "undefined" || !window.requestAnimationFrame) return () => {};
    let raf = 0;
    let start = null;
    let taken = false;
    let bound = null;
    const stop = () => {
        if (raf) window.cancelAnimationFrame(raf);
        raf = 0;
        if (bound) for (const t of READER_TAKEOVER_EVENTS) bound.removeEventListener(t, take);
        bound = null;
    };
    function take() { taken = true; stop(); }
    const step = (now) => {
        if (taken) return;
        if (start == null) start = now;
        const el = typeof getEl === "function" ? getEl() : getEl;
        const viewport = el && typeof el.getBoundingClientRect === "function" ? findScrollParent(el) : null;
        if (viewport) {
            if (!bound) {
                bound = viewport;
                for (const t of READER_TAKEOVER_EVENTS) viewport.addEventListener(t, take, { passive: true });
            }
            scrollRowIntoView(el, align);
        }
        if (now - start < budgetMs) raf = window.requestAnimationFrame(step);
        else stop();
    };
    raf = window.requestAnimationFrame(step);
    return stop;
}
